// Phase II checklist on the Landing milestone banner — mirrors the admin
// Charter Readiness scorecard (auto-measured items tick themselves).
import { useEffect, useState } from "react";
import { CalendarClock, CheckCircle2, ChevronDown, Circle, CircleDot, MinusCircle } from "lucide-react";
import api from "../lib/api";

const STATUS = {
  complete: { Icon: CheckCircle2, cls: "text-emerald-400", text: "text-bdu-cream", label: "Done" },
  in_progress: { Icon: CircleDot, cls: "text-bdu-gold", text: "text-bdu-cream/85", label: "In progress" },
  not_started: { Icon: Circle, cls: "text-bdu-cream/30", text: "text-bdu-cream/55", label: "Not started" },
  not_applicable: { Icon: MinusCircle, cls: "text-bdu-cream/25", text: "text-bdu-cream/35 line-through", label: "Not needed" },
};

function Countdown({ deadline, label }) {
  const days = Math.max(0, Math.ceil((new Date(`${deadline}T23:59:59-04:00`).getTime() - Date.now()) / 86400000));
  return (
    <div className="inline-flex items-center gap-2 border border-bdu-gold/40 bg-black/50 px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-bdu-cream/80"
         data-testid="phase2-deadline">
      <CalendarClock size={14} className="text-bdu-gold" />
      Phase 2 due to NCUA by {label}
      <span className="text-bdu-gold font-semibold" data-testid="phase2-days-left">· {days.toLocaleString()} days left</span>
    </div>
  );
}

function SectionCard({ section, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-bdu-gold/20 bg-black/50 backdrop-blur-sm text-left" data-testid={`phase2-section-${section.key}`}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-3 px-4 py-3"
              aria-expanded={open} data-testid={`phase2-section-${section.key}-toggle`}>
        <span className="text-[11px] uppercase tracking-[0.2em] text-bdu-gold font-semibold">{section.title}</span>
        <ChevronDown size={14} className={`text-bdu-cream/50 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul className="px-4 pb-4 space-y-2">
          {section.items.map((it) => {
            const s = STATUS[it.status] || STATUS.not_started;
            return (
              <li key={it.id} className="flex items-start gap-2 text-[13px] leading-snug" data-testid={`phase2-item-${it.id}`} data-status={it.status}>
                <s.Icon size={15} className={`${s.cls} shrink-0 mt-px`} aria-label={s.label} />
                <span className={s.text}>{it.title}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function Phase2Tracker() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/milestone/phase2-tracker").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;
  const wide = typeof window !== "undefined" && window.innerWidth >= 640;
  return (
    <div className="mt-12" data-testid="phase2-tracker">
      <div className="flex flex-col items-center gap-4 text-center">
        <div className="text-[10px] uppercase tracking-[0.32em] text-bdu-gold/80">Phase II · Charter Readiness Checklist</div>
        <h3 className="font-serif text-2xl sm:text-3xl text-bdu-cream">What we're finishing before we submit to NCUA</h3>
        <Countdown deadline={data.deadline} label={data.deadline_label} />
      </div>
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
        {data.sections.map((s) => <SectionCard key={s.key} section={s} defaultOpen={wide} />)}
      </div>
      <div className="mt-4 flex flex-wrap justify-center gap-5 text-[11px] text-bdu-cream/55" data-testid="phase2-legend">
        {["complete", "in_progress", "not_started"].map((k) => {
          const { Icon, cls, label } = STATUS[k];
          return <span key={k} className="inline-flex items-center gap-1.5"><Icon size={13} className={cls} /> {label}</span>;
        })}
      </div>
    </div>
  );
}
